"use client";

import Footer from "@/components/Footer";
import Navigation from "@/components/Navigation";
import { useEffect } from "react";

export default function HostingPage() {
  useEffect(() => {
    const reveals = Array.from(document.querySelectorAll<HTMLElement>(".reveal"));
    if (!reveals.length) return;

    // Respect reduced motion
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion) {
      reveals.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;

          // Fade in when in view, fade out when out of view
          if (entry.isIntersecting) el.classList.add("is-visible");
          else el.classList.remove("is-visible");
        });
      },
      {
        threshold: 0.15,
        // Slightly earlier trigger so animation feels smoother
        rootMargin: "0px 0px -10% 0px",
      }
    );

    reveals.forEach((el) => observer.observe(el));

    // Ensure above-the-fold items animate on first load
    reveals.forEach((el) => {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) el.classList.add("is-visible");
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-background" style={{ fontFamily: "'Roboto', sans-serif" }}>
      {/* ✅ Only animation CSS added. Design/content unchanged */}
      <style>{`
        .reveal {
          opacity: 0;
          transform: translate3d(0, 18px, 0);
          filter: blur(6px);
          transition: opacity 700ms ease, transform 700ms ease, filter 700ms ease;
          will-change: opacity, transform, filter;
        }

        .reveal.is-visible {
          opacity: 1;
          transform: translate3d(0, 0, 0);
          filter: blur(0px);
        }

        @media (prefers-reduced-motion: reduce) {
          .reveal,
          .reveal.is-visible {
            transition: none !important;
            transform: none !important;
            filter: none !important;
            opacity: 1 !important;
          }
        }
      `}</style>

      <Navigation />

      {/* Hero Section - Dark Theme */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden bg-gradient-to-br from-[#040814] via-[#0A0F24] to-[#131B35] text-white py-24">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-blue-900/30 via-transparent to-transparent"></div>
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-gradient-radial from-blue-500/5 via-transparent to-transparent"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* LEFT CONTENT */}
            <div className="reveal space-y-8 pl-4 lg:pl-8">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">
                Your Website Deserves The Best & The Most{" "}
                <span className="bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
                  Secure Web Hosting
                </span>
              </h1>

              {/* Smaller text here */}
              <p className="text-base text-slate-300 leading-relaxed max-w-2xl">
                A web hosting environment in which more than one website can be hosted on a single web server is called as
                shared hosting. These hosting accounts share the server resources like CPU, memory, and disk space. In
                simple language, multiple hosting accounts are created on a single server and those are assigned to
                individual websites or domain names. Startup or small business websites receive moderate traffic and so
                minimal amount of server resources are required to host such websites. For example, you travel by bus,
                where you need to share your seat with others. You choose this option as it is affordable as compared to
                taxi or your own car. Therefore, it is the most affordable form of hosting for small and startup
                businesses.
              </p>

              {/* Buttons */}
              <div className="flex flex-wrap gap-3 pt-4">
                <a
                  href="#plans"
                  className="group relative px-5 py-2.5 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg font-semibold text-sm text-white shadow-[0_6px_18px_rgba(79,70,229,0.25)] hover:shadow-[0_8px_24px_rgba(79,70,229,0.4)] transition-all duration-300 overflow-hidden"
                >
                  <span className="relative z-10">Explore Premium Plans</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-700 to-purple-700 translate-y-full group-hover:translate-y-0 transition-transform duration-300"></div>
                </a>

                <a
                  href="#features"
                  className="group px-5 py-2.5 bg-white/10 backdrop-blur-sm rounded-lg font-semibold text-sm text-white border border-white/20 hover:bg-white/20 hover:border-white/40 transition-all duration-300"
                >
                  <span className="flex items-center gap-1.5">
                    Explore Features
                    <svg
                      className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </span>
                </a>
              </div>
            </div>

            {/* RIGHT IMAGE */}
            <div className="reveal relative lg:pl-16 translate-x-4">
              <div className="relative z-10">
                <img
                  src="/hosting1.png"
                  alt="Hosting Dashboard Interface"
                  className="rounded-3xl shadow-2xl transform hover:scale-[1.02] transition-transform duration-500"
                />
              </div>

              <div className="absolute -top-6 -left-6 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-6 -right-6 w-64 h-64 bg-purple-500/20 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Plans Section - Light Theme */}
      <section id="plans" className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 sm:px-6">
          {/* Header */}
          <header className="reveal text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span>✨</span>
              <span>India's Best Shared Hosting Plans</span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">India's Best Shared Hosting Plans</h2>

            <p className="text-lg text-slate-600">Same plan names and features — clean layout with highlighted middle plan.</p>
          </header>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* LITE */}
            <article className="reveal group relative bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-slate-200">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="bg-slate-100 text-slate-700 px-4 py-1 rounded-full text-sm font-semibold border border-slate-300">
                  Starter
                </div>
              </div>

              <div className="text-left mb-8 pt-4">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">LITE</h3>
              </div>

              <ul className="space-y-4 mb-10 text-slate-700">
                <li>Prices</li>
                <li>Host Websites</li>
                <li>Bandwidth</li>
                <li>SSL Certificates</li>
                <li>cPanel</li>
                <li>Email Accounts</li>
                <li>MySQL Databases</li>
              </ul>

              <a
                href="/getintouch"
                className="block text-center bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-3 font-semibold transition-all duration-300"
              >
                Buy Now
              </a>
            </article>

            {/* DELUXE (Highlighted) */}
            <article className="reveal group relative bg-white rounded-2xl p-8 shadow-2xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-2 border-2 border-indigo-300">
              <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                <div className="bg-indigo-600 text-white px-6 py-1.5 rounded-full text-sm font-semibold shadow-lg">
                  MOST POPULAR
                </div>
              </div>

              <div className="text-left mb-8 pt-6">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">DELUXE</h3>
              </div>

              <ul className="space-y-4 mb-10 text-slate-700">
                <li>Prices</li>
                <li>Host Websites</li>
                <li>Bandwidth</li>
                <li>SSL Certificates</li>
                <li>cPanel</li>
                <li>Email Accounts</li>
                <li>MySQL Databases</li>
              </ul>

              <a
                href="/getintouch"
                className="block text-center bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-3 font-semibold transition-all duration-300"
              >
                Buy Now
              </a>
            </article>

            {/* PREMIUM */}
            <article className="reveal group relative bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-slate-200">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2">
                <div className="bg-slate-100 text-slate-700 px-4 py-1 rounded-full text-sm font-semibold border border-slate-300">
                  Power
                </div>
              </div>

              <div className="text-left mb-8 pt-4">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">PREMIUM</h3>
              </div>

              <ul className="space-y-4 mb-10 text-slate-700">
                <li>Prices</li>
                <li>Host Websites</li>
                <li>Bandwidth</li>
                <li>SSL Certificates</li>
                <li>cPanel</li>
                <li>Email Accounts</li>
                <li>MySQL Databases</li>
              </ul>

              <a
                href="/getintouch"
                className="block text-center bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-3 font-semibold transition-all duration-300"
              >
                Buy Now
              </a>
            </article>
          </div>
        </div>
      </section>

      {/* Features Section - Dark Theme */}
      <section id="features" className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <div className="container mx-auto px-4 sm:px-6">
          <header className="reveal text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-blue-900/30 text-blue-300 px-4 py-2 rounded-full text-sm font-medium mb-6 backdrop-blur-sm">
              <span>✅</span>
              <span>Best hosting features</span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Everything You Need to Start a{" "}
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Website</span>
            </h2>

            <p className="text-lg text-slate-300">Best hosting features - 24/7 support.</p>
          </header>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {[
              {
                icon: "🔒",
                title: "Free SSL Certificate",
                desc: "Our linux shared web hosting plan comes incorporated with SSL certificate. We ensure that all the communication that takes place between you and your visitor’s browser stays secure.",
                color: "from-blue-500 to-cyan-500",
              },
              {
                icon: "🧰",
                title: "Panel Control Panel",
                desc: "Our Linux shared hosting offers an extensive range of features that lets you administer every aspect of your website from a web-based interface. You can easily manage files, emails, databases, domain(s).",
                color: "from-purple-500 to-pink-500",
              },
              {
                icon: "🗺️",
                title: "Datacenter Choice",
                desc: "With regards to choosing where your website data will reside, there are few important factors to consider, picking the right datacenter is one of them. Choose your preferred location at the time of sign-up.",
                color: "from-emerald-500 to-teal-500",
              },
              {
                icon: "⚡",
                title: "Easy Upgrades",
                desc: "Begin with our cheap shared hosting plan and later when your site grows, you can easily move to more powerful options. Our advanced technology lets you upgrade effortlessly just in few steps.",
                color: "from-orange-500 to-red-500",
              },
              {
                icon: "🕘",
                title: "24/7/365 Support",
                desc: "Call us, initiate a live chat or drop an email. Our in-house support team will fix all your hosting related issues.",
                color: "from-cyan-500 to-blue-500",
              },
              {
                icon: "💸",
                title: "30 Day Money Back",
                desc: "Absolutely no risk in trying our services. If you are not 100% satisfied, we'll happily refund your money!",
                color: "from-yellow-500 to-orange-500",
              },
              {
                icon: "📈",
                title: "99.9% Uptime",
                desc: "Our top-most priority is the availability of your website. Our Tier-3 and Tier-4 data centers enables us to provide you the best uptime.",
                color: "from-indigo-500 to-blue-500",
              },
              {
                icon: "🚚",
                title: "Free Website Migration",
                desc: "Moving a website from existing hosting provider to MilesWeb server is not a big deal as we have our team of experts to do it for you.",
                color: "from-pink-500 to-rose-500",
              },
            ].map((feature, index) => (
              <div key={index} className="reveal group">
                <div className="relative h-full bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-slate-600 transition-all duration-300 hover:bg-slate-800/70">
                  <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${feature.color} rounded-t-2xl`} />

                  <div className="flex items-start gap-4 mb-4">
                    <div className={`text-2xl p-3 rounded-xl bg-gradient-to-br ${feature.color} bg-opacity-20`}>
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold text-white leading-snug">{feature.title}</h3>
                  </div>

                  <p className="text-slate-300 text-sm leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Types of Hosting Section - Light Theme */}
      <section className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="container mx-auto px-4 sm:px-6">
          <header className="reveal text-center max-w-4xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-100 to-purple-100 text-slate-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span>📚</span>
              <span>Hosting Knowledge</span>
            </div>

            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Types of Web Hosting{" "}
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">in India</span>
            </h2>

            <p className="text-lg text-slate-600">
              Different hosting types fit different needs. Choose based on traffic, control, performance, and budget.
            </p>
          </header>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: "Shared Hosting",
                desc: "Best for small websites and beginners. Low cost, easy to manage, but limited resources and not ideal for high traffic.",
                features: [],
                color: "border-blue-200 bg-blue-50",
                icon: "🏠",
              },
              {
                title: "VPS Hosting",
                desc: "More power and control than shared hosting. Scalable and customizable, suitable for growing sites and businesses.",
                features: [],
                color: "border-green-200 bg-green-50",
                icon: "🖥️",
              },
              {
                title: "Dedicated Server Hosting",
                desc: "A full server for your website. High performance, maximum control, and best for large businesses with heavy traffic.",
                features: [],
                color: "border-orange-200 bg-orange-50",
                icon: "🚀",
              },
              {
                title: "Cloud Hosting",
                desc: "Runs on multiple servers for better reliability. Great for flexibility and scaling, ideal for variable traffic websites.",
                features: [],
                color: "border-purple-200 bg-purple-50",
                icon: "☁️",
              },
              {
                title: "Reseller Hosting",
                desc: "Start your own hosting business. Buy resources in bulk and sell to clients with your branding and packages.",
                features: [],
                color: "border-pink-200 bg-pink-50",
                icon: "💰",
              },
              {
                title: "Managed WordPress Hosting",
                desc: "WordPress-optimized hosting with updates, backups, speed, and security handled by the provider. Less control, more convenience.",
                features: [],
                color: "border-cyan-200 bg-cyan-50",
                icon: "⚡",
              },
            ].map((type, index) => (
              <div key={index} className="reveal group">
                <div
                  className={`relative h-full rounded-2xl p-6 border-2 ${type.color} transition-all duration-500 hover:shadow-2xl hover:-translate-y-1`}
                >
                  <div className="flex items-start gap-4">
                    <div className="text-3xl p-3 rounded-xl bg-white shadow-sm">{type.icon}</div>

                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-slate-900 mb-2">{type.title}</h3>

                      <p className="text-slate-600 leading-relaxed">{type.desc}</p>

                      {type.features?.length ? (
                        <div className="flex flex-wrap gap-2 mt-4">
                          {type.features.map((feature, fIndex) => (
                            <span
                              key={fIndex}
                              className="px-3 py-1 bg-white rounded-full text-sm font-medium text-slate-700 border border-slate-200"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
